import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { getLoginRequest, getMsal } from './auth'
import { DEV_AUTH_BYPASS } from './devAuth'

// Same origin: nginx proxies /api to the internal API container (ADR-001), and
// the Vite dev server proxies it to localhost:4000.
const API_BASE = ''

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiRequestError'
  }
}

async function getAccessToken(): Promise<string> {
  const msal = getMsal()
  const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0]
  if (!account) {
    await msal.acquireTokenRedirect(getLoginRequest())
    throw new Error('Redirecting to login')
  }

  try {
    const response = await msal.acquireTokenSilent({ ...getLoginRequest(), account })
    return response.accessToken
  } catch (err) {
    // Refresh token expired or interaction required: redirect to Entra login
    if (err instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect({ ...getLoginRequest(), account })
      throw new Error('Redirecting to login')
    }
    throw err
  }
}

/** The Authorization header for API calls; none in local dev without Entra. */
export async function authHeaders(): Promise<Record<string, string>> {
  if (DEV_AUTH_BYPASS) return {}
  return { Authorization: `Bearer ${await getAccessToken()}` }
}

interface FetchOptions extends Omit<RequestInit, 'headers' | 'body'> {
  headers?: Record<string, string>
  /** Serialised as JSON. */
  json?: unknown
}

export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { json, headers, ...rest } = options
  const response = await fetch(`${API_BASE}${path}`, {
    ...rest,
    ...(json !== undefined && { body: JSON.stringify(json) }),
    headers: {
      ...(json !== undefined && { 'Content-Type': 'application/json' }),
      ...(await authHeaders()),
      ...headers,
    },
  })

  if (response.status === 401 && DEV_AUTH_BYPASS) {
    throw new ApiRequestError('The API rejected local sign-in: set DEV_AUTH_BYPASS=true for the API too', 401)
  }

  if (response.status === 401) {
    // Token was rejected server-side: force a fresh login
    await getMsal().acquireTokenRedirect(getLoginRequest())
    throw new ApiRequestError('Redirecting to login', 401)
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: unknown }
    const message = typeof body.message === 'string' ? body.message : `HTTP ${response.status}`
    throw new ApiRequestError(message, response.status)
  }

  if (response.status === 204 || response.status === 205) return undefined as T
  return response.json() as Promise<T>
}

/** Builds a query string, dropping empty values. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  }
  const s = search.toString()
  return s ? `?${s}` : ''
}
